import type { ReactElement } from "react";
import { useCallback, useMemo, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Combobox, DropdownButton } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { StyledBaseDropdownButtonWrap } from "app/pages/sales-intelligence/common-components/styles";

import trackingService from "../../../advanced-search/services/tracking/advancedSearchTrackingService";
import { DROPDOWN_BUTTON_TEXT_LENGTH_LIMIT } from "../../constants/common";
import { getSeparatedValuesPlusN } from "../../helpers/common";

type DepartmentsDropdownProps = {
    items: readonly string[];
    selectedIds?: readonly string[];
    isDisabled?: boolean;
    onSelect(selectedIds: readonly string[]): void;
};

const DepartmentsDropdown = ({
    items,
    selectedIds = [],
    isDisabled = false,
    onSelect,
}: DepartmentsDropdownProps): ReactElement => {
    const { translate } = useTranslate();
    const [isOpen, setIsOpen] = useState(false);

    const buttonText = useMemo(() => {
        if (selectedIds.length === 0) {
            return translate("si.contacts-search-profiles.department-dropdown.no-items");
        }
        if (selectedIds.length === items.length) {
            return translate("si.contacts-search-profiles.department-dropdown.all-items");
        }
        return getSeparatedValuesPlusN(selectedIds, DROPDOWN_BUTTON_TEXT_LENGTH_LIMIT);
    }, [selectedIds, items.length, translate]);

    const handleSelect = useCallback(
        (id: string) => {
            if (selectedIds.includes(id)) {
                trackingService.contactsFilters.trackProfileDepartmentRemoved(id);
                onSelect(selectedIds.filter((s) => s !== id));
            } else {
                trackingService.contactsFilters.trackProfileDepartmentAdded(id);
                onSelect([...selectedIds, id]);
            }
        },
        [selectedIds, onSelect],
    );

    if (isDisabled) {
        return (
            <StyledBaseDropdownButtonWrap>
                <DropdownButton
                    disabled
                    hideArrow
                >
                    {buttonText}
                </DropdownButton>
            </StyledBaseDropdownButtonWrap>
        );
    }

    return (
        <Combobox
            isOpen={isOpen}
            onOpenChange={setIsOpen}
            placement="bottom-start"
        >
            <Combobox.Trigger>
                <StyledBaseDropdownButtonWrap>
                    <DropdownButton>{buttonText}</DropdownButton>
                </StyledBaseDropdownButtonWrap>
            </Combobox.Trigger>

            <Combobox.Content>
                {items.map((item) => (
                    <Combobox.Option
                        key={item}
                        id={item}
                        textValue={item}
                        onClick={() => handleSelect(item)}
                    >
                        <Flex
                            alignItems="center"
                            justifyContent="space-between"
                            gap="8px"
                            width="100%"
                        >
                            <Combobox.OptionLabel>{item}</Combobox.OptionLabel>
                            <Combobox.OptionCheckbox isChecked={selectedIds.includes(item)} />
                        </Flex>
                    </Combobox.Option>
                ))}
            </Combobox.Content>
        </Combobox>
    );
};

export default DepartmentsDropdown;
